
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import { translations as staticTranslations } from "../i18n/translations";
import { api } from "../lib/api";

// =====================================
// LANGUAGE SETTINGS
// =====================================

const LanguageContext = createContext(null);

export const SUPPORTED_LANGUAGES = [
  "en",
  "al",
  "it",
  "es",
];

const DEFAULT_LANGUAGE = "en";
const STORAGE_KEY = "frontspace-language";

// =====================================
// DEEP MERGE TRANSLATIONS
// =====================================

function mergeDeep(base, override) {
  if (override === undefined || override === null) {
    return base;
  }

  if (Array.isArray(override)) {
    return override;
  }

  if (
    override &&
    typeof override === "object" &&
    !Array.isArray(override)
  ) {
    const baseObject =
      base &&
      typeof base === "object" &&
      !Array.isArray(base)
        ? base
        : {};

    const result = { ...baseObject };

    for (const key of Object.keys(override)) {
      result[key] = mergeDeep(
        baseObject[key],
        override[key]
      );
    }

    return result;
  }

  return override;
}

// =====================================
// GET SAVED LANGUAGE
// =====================================

function getInitialLanguage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);

    if (SUPPORTED_LANGUAGES.includes(saved)) {
      return saved;
    }
  } catch (error) {
    // Browser storage unavailable.
  }

  return DEFAULT_LANGUAGE;
}

// =====================================
// LANGUAGE PROVIDER
// =====================================

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(
    getInitialLanguage
  );

  const [overrides, setOverrides] = useState(null);

  const [translationsLoading, setTranslationsLoading] =
    useState(true);

  // =====================================
  // CHANGE LANGUAGE
  // =====================================

  const setLanguage = useCallback((newLanguage) => {
    if (!SUPPORTED_LANGUAGES.includes(newLanguage)) {
      return;
    }

    setLanguageState(newLanguage);

    try {
      localStorage.setItem(
        STORAGE_KEY,
        newLanguage
      );
    } catch (error) {
      // Browser storage unavailable.
    }
  }, []);

  // =====================================
  // LOAD TRANSLATIONS FROM BACKEND
  // =====================================

  const refreshTranslations = useCallback(async () => {
    try {
      const data = await api.getContent();

      if (data && typeof data === "object") {
        setOverrides(data);
      }
    } catch (error) {
      console.warn(
        "Unable to load translated content. Using local translations.",
        error
      );
    } finally {
      setTranslationsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshTranslations();
  }, [refreshTranslations]);

  // =====================================
  // UPDATE HTML LANGUAGE
  // =====================================

  useEffect(() => {
    document.documentElement.dir = "ltr";

    // Albanian language code in HTML is "sq".
    document.documentElement.lang =
      language === "al" ? "sq" : language;
  }, [language]);

  // =====================================
  // COMBINE STATIC AND DYNAMIC CONTENT
  // =====================================

  const t = useMemo(() => {
    const englishBase =
      staticTranslations.en || {};

    const languageBase =
      staticTranslations[language] || {};

    // English acts as a fallback when a
    // translated key is missing.
    const base = mergeDeep(
      englishBase,
      languageBase
    );

    const englishOverrides =
      overrides?.en || {};

    const languageOverrides =
      overrides?.[language] || {};

    const withEnglishOverrides = mergeDeep(
      base,
      englishOverrides
    );

    return mergeDeep(
      withEnglishOverrides,
      languageOverrides
    );
  }, [language, overrides]);

  // =====================================
  // CONTEXT VALUES
  // =====================================

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      translationsLoading,
      refreshTranslations,
      supportedLanguages: SUPPORTED_LANGUAGES,
    }),
    [
      language,
      setLanguage,
      t,
      translationsLoading,
      refreshTranslations,
    ]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

// =====================================
// CUSTOM HOOK
// =====================================

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error(
      "useLanguage must be used inside LanguageProvider"
    );
  }

  return context;
}
